import React from "react";
import type { ZekayCustomer } from "../../api/customers/Customer.typed";
import type { UserWithRole } from "../../api/auth/Auth.typed";
import type { PaginatedResponse } from "../../api/zekay/pagination";
import { useCustomer, useAuth, useQualification } from "../api";
import useAppSettings from "./appSettings";
import { userDisplayName } from "../../utils/userHelper";

export type ExcursionModalUser = {
  id: number;
  name: string;
  first_name?: string;
  last_name?: string;
  qualification?: string;
  phone_number?: string;
};

const DEFAULT_CUSTOMERS_LIST_PAGE_SIZE = 20;

export interface CustomersDataContextValue {
  customers: ZekayCustomer[];
  setCustomers: React.Dispatch<React.SetStateAction<ZekayCustomer[]>>;
  selectedCustomer: ZekayCustomer | undefined;
  profileFormOpen: boolean;
  isDesync: boolean;
  excursionModalUsers: ExcursionModalUser[];
  openProfile: (customer: ZekayCustomer) => void;
  openCreateProfile: () => void;
  closeProfile: () => void;
  updateCustomerInList: (updated: ZekayCustomer) => void;
  handleCustomerCreated: (created: ZekayCustomer) => void;
  handleDeleteCustomer: (customer: ZekayCustomer) => Promise<void>;
  setSelectedCustomer: React.Dispatch<React.SetStateAction<ZekayCustomer | undefined>>;
  listPage: number;
  setListPage: React.Dispatch<React.SetStateAction<number>>;
  listTotal: number;
  listTotalPages: number;
  listPageSize: number;
  setListPageSize: React.Dispatch<React.SetStateAction<number>>;
  listLoading: boolean;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  qualificationFilter: string;
  setQualificationFilter: React.Dispatch<React.SetStateAction<string>>;
  qualificationOptions: string[];
  refetchCustomerList: () => void;
  isListQueryActive: boolean;
}

function mapUsersForModal(list: UserWithRole[]): ExcursionModalUser[] {
  return list.map((u) => ({
    id: u.user.id,
    name: userDisplayName(u.user) || `Utilisateur #${u.user.id}`,
    first_name: u.user.first_name?.trim() || undefined,
    last_name: u.user.last_name?.trim() || undefined,
    qualification: u.user.last_qualification?.trim() || undefined,
    phone_number: u.user.phone_number?.trim() || undefined,
  }));
}

const CustomersDataContext = React.createContext<CustomersDataContextValue | undefined>(undefined);

export function CustomersDataProvider({ children }: { children: React.ReactNode }) {
  const { config } = useAppSettings();
  const { deleteCustomer, getCustomer, getCustomers } = useCustomer();
  const { getQualificationsList } = useQualification();
  const { getUsers } = useAuth();

  const isDesync = config?.CustomersSync === false;

  const [customers, setCustomers] = React.useState<ZekayCustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = React.useState<ZekayCustomer>();
  const [profileFormOpen, setProfileFormOpen] = React.useState(false);
  const [excursionModalUsers, setExcursionModalUsers] = React.useState<ExcursionModalUser[]>([]);
  const [listPage, setListPage] = React.useState(1);
  const [listPageSize, setListPageSize] = React.useState(DEFAULT_CUSTOMERS_LIST_PAGE_SIZE);
  const [listTotal, setListTotal] = React.useState(0);
  const [listLoading, setListLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debSearch, setDebSearch] = React.useState("");
  const [qualificationFilter, setQualificationFilter] = React.useState("");
  const [qualificationOptions, setQualificationOptions] = React.useState<string[]>([]);
  const skipPageReset = React.useRef(true);

  React.useEffect(() => {
    getUsers().then((list) => {
      if (list) setExcursionModalUsers(mapUsersForModal(list));
    });
  }, [getUsers]);

  React.useEffect(() => {
    getQualificationsList().then((sigles) => {
      if (sigles) setQualificationOptions(sigles);
    });
  }, [getQualificationsList]);

  React.useEffect(() => {
    const t = setTimeout(() => setDebSearch(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const isListQueryActive = debSearch.trim().length >= 2 || qualificationFilter.trim() !== "";

  const listParams = React.useMemo(
    () => ({
      page: listPage,
      limit: listPageSize,
      sort_order: "asc" as const,
      q: debSearch.trim().length >= 2 ? debSearch.trim() : undefined,
      qualification: qualificationFilter || undefined,
    }),
    [listPage, listPageSize, debSearch, qualificationFilter],
  );

  const listTotalPages = listTotal > 0 ? Math.ceil(listTotal / listPageSize) : 0;

  const refetchCustomerList = React.useCallback(() => {
    if (!isListQueryActive) {
      setCustomers([]);
      setListTotal(0);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    getCustomers(listParams)
      .then((res) => {
        if (res && res !== null && typeof res === "object" && "data" in res) {
          const p = res as PaginatedResponse<ZekayCustomer>;
          setCustomers(p.data);
          setListTotal(p.total);
          const maxP = p.total > 0 ? Math.ceil(p.total / listParams.limit) : 1;
          setListPage((pg) => (p.total === 0 ? 1 : Math.min(Math.max(1, pg), maxP)));
        } else {
          setCustomers([]);
          setListTotal(0);
        }
      })
      .finally(() => {
        setListLoading(false);
      });
  }, [getCustomers, listParams, isListQueryActive]);

  React.useEffect(() => {
    if (skipPageReset.current) {
      skipPageReset.current = false;
    } else {
      setListPage(1);
    }
  }, [debSearch, qualificationFilter]);

  React.useLayoutEffect(() => {
    if (!isListQueryActive) {
      setCustomers([]);
      setListTotal(0);
      setListLoading(false);
      return;
    }
    void refetchCustomerList();
  }, [isListQueryActive, refetchCustomerList]);

  const openProfile = React.useCallback(
    (customer: ZekayCustomer) => {
      if (customer.Id) {
        getCustomer(String(customer.Id)).then((full) => {
          setSelectedCustomer(full || customer);
          setProfileFormOpen(true);
        });
      } else {
        setSelectedCustomer(customer);
        setProfileFormOpen(true);
      }
    },
    [getCustomer],
  );

  const closeProfile = React.useCallback(() => {
    setProfileFormOpen(false);
    setSelectedCustomer(undefined);
  }, []);

  const openCreateProfile = React.useCallback(() => {
    const draft: ZekayCustomer = {
      FirstName: "",
      LastName: "",
      Birthdate: undefined as unknown as Date,
      Street: undefined,
      PostalCode: undefined,
      City: undefined,
      Phonenumber: undefined,
      MailAddress: undefined,
      GdprConsent: false,
      Photo: undefined,
      MedicalCertificate: undefined,
    };
    setSelectedCustomer(draft);
    setProfileFormOpen(true);
  }, []);

  const updateCustomerInList = React.useCallback((updated: ZekayCustomer) => {
    setSelectedCustomer(updated);
    setCustomers((prev) => prev.map((cst) => (cst.Id === updated.Id ? updated : cst)));
  }, []);

  const handleCustomerCreated = React.useCallback(
    (_created: ZekayCustomer) => {
      const canLoad = debSearch.trim().length >= 2 || qualificationFilter.trim() !== "";
      if (!canLoad) {
        setListPage(1);
        return;
      }
      setListPage(1);
      setListLoading(true);
      getCustomers({
        page: 1,
        limit: listPageSize,
        sort_order: "asc",
        q: debSearch.trim().length >= 2 ? debSearch.trim() : undefined,
        qualification: qualificationFilter || undefined,
      })
        .then((res) => {
          if (res && res !== null && typeof res === "object" && "data" in res) {
            const p = res as PaginatedResponse<ZekayCustomer>;
            setCustomers(p.data);
            setListTotal(p.total);
          }
        })
        .finally(() => {
          setListLoading(false);
        });
    },
    [getCustomers, debSearch, qualificationFilter, listPageSize],
  );

  const handleDeleteCustomer = React.useCallback(
    async (customer: ZekayCustomer) => {
      if (customer.Id == null) return;
      await deleteCustomer(customer.Id);
      if (debSearch.trim().length >= 2 || qualificationFilter.trim() !== "") {
        void refetchCustomerList();
      } else {
        setCustomers([]);
        setListTotal(0);
      }
    },
    [deleteCustomer, refetchCustomerList, debSearch, qualificationFilter],
  );

  const value = React.useMemo<CustomersDataContextValue>(
    () => ({
      customers,
      setCustomers,
      selectedCustomer,
      profileFormOpen,
      isDesync,
      excursionModalUsers,
      openProfile,
      openCreateProfile,
      closeProfile,
      updateCustomerInList,
      handleCustomerCreated,
      handleDeleteCustomer,
      setSelectedCustomer,
      isListQueryActive,
      listPage,
      setListPage,
      listTotal,
      listTotalPages,
      listPageSize,
      setListPageSize,
      listLoading,
      searchQuery,
      setSearchQuery,
      qualificationFilter,
      setQualificationFilter,
      qualificationOptions,
      refetchCustomerList,
    }),
    [
      customers,
      selectedCustomer,
      profileFormOpen,
      isDesync,
      excursionModalUsers,
      openProfile,
      openCreateProfile,
      closeProfile,
      updateCustomerInList,
      handleCustomerCreated,
      handleDeleteCustomer,
      isListQueryActive,
      listPage,
      listTotal,
      listTotalPages,
      listPageSize,
      setListPageSize,
      listLoading,
      searchQuery,
      qualificationFilter,
      qualificationOptions,
      refetchCustomerList,
    ],
  );

  return <CustomersDataContext.Provider value={value}>{children}</CustomersDataContext.Provider>;
}

export function useCustomersData(): CustomersDataContextValue {
  const ctx = React.useContext(CustomersDataContext);
  if (!ctx) throw new Error("useCustomersData must be used within CustomersDataProvider");

  return ctx;
}

export function useExcursionModalUsers(): ExcursionModalUser[] {
  const ctx = React.useContext(CustomersDataContext);
  const { getUsers } = useAuth();
  const [fallback, setFallback] = React.useState<ExcursionModalUser[]>([]);

  React.useEffect(() => {
    if (ctx !== undefined) {
      return;
    }

    let cancelled = false;
    void getUsers().then((list) => {
      if (cancelled || !list) {
        return;
      }

      setFallback(mapUsersForModal(list));
    });

    return () => {
      cancelled = true;
    };
  }, [ctx, getUsers]);

  if (ctx !== undefined) {
    return ctx.excursionModalUsers;
  }

  return fallback;
}
